/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Spinner from 'components/Spinner/Spinner';
import './Input.scss';

/**
 * This is the Input component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Input({
  innerRef,
  className,
  icon,
  loading,
  loadingText,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  ...props
}) {
  const [focus, setFocus] = useState(false);
  const [hover, setHover] = useState(false);

  function renderIcon() {
    if (loading) {
      return <Spinner title={loadingText} />;
    }

    return icon;
  }

  return (
    <div className={classnames('field-row', className, { focus, hover })}>
      <input
        ref={innerRef}
        {...props}
        onFocus={e => {
          setFocus(true);
          onFocus(e);
        }}
        onBlur={e => {
          setFocus(false);
          onBlur(e);
        }}
        onMouseEnter={e => {
          setHover(true);
          onMouseEnter(e);
        }}
        onMouseLeave={e => {
          setHover(false);
          onMouseLeave(e);
        }}
      />
      {renderIcon()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Input.propTypes = {
  innerRef: PropTypes.object,
  className: PropTypes.string,
  icon: PropTypes.object,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Input.defaultProps = {
  innerRef: null,
  className: null,
  icon: null,
  loading: false,
  loadingText: 'Loading',
  onFocus: () => {},
  onBlur: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
};

export default forwardRef((props, ref) => <Input innerRef={ref} {...props} />);
